/**
 * Tiny fetch wrapper used for every API call from the web.
 *
 * Design:
 *  - Access token lives in module memory (never localStorage) — survives a
 *    tab reload via the silent refresh on app boot.
 *  - Refresh token is in an httpOnly cookie scoped to /api/auth — invisible
 *    to JS by design.
 *  - On a 401 from the API the wrapper attempts a refresh once; if that
 *    succeeds it retries the original request, otherwise it clears the
 *    in-memory token and emits an `auth:logout` event so the AuthProvider
 *    can redirect to /login.
 */

const API_BASE = '/api';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown) {
    super(`API ${status}`);
  }
}

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipRefresh?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, headers, skipRefresh, ...rest } = opts;
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  let res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 401 && !skipRefresh && path !== '/auth/refresh') {
    const ok = await refreshOnce();
    if (ok) {
      res = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: { ...init.headers, Authorization: `Bearer ${accessToken}` },
      });
    } else {
      window.dispatchEvent(new Event('auth:logout'));
    }
  }

  const text = await res.text();
  const parsed = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, parsed ?? text);
  return parsed as T;
}

async function refreshOnce(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Fetch an authenticated file endpoint and trigger a browser download.
 * Used for report PDFs/XLSX, which are streamed behind auth so a plain
 * anchor href would 401.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null));

  // Prefer the server's filename from Content-Disposition.
  const disp = res.headers.get('content-disposition') ?? '';
  const match = disp.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? fallbackName;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch a file with auth and return a local object URL (blob:) for previewing
 * it in-app (e.g. a PDF in an <iframe>). The caller MUST revokeObjectURL when
 * done. Blob URLs are same-origin, so they embed without X-Frame-Options fuss.
 */
export async function fetchBlobUrl(path: string): Promise<{ url: string; type: string }> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null));
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}
